import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { Field, StatCard, TableWrap, Empty, th, td, tr } from "@/components/common";

const LABELS = { date: "Date *", slip_no: "Slip / Bill No", vrn: "Vehicle No", litres: "Litres", debit: "Debit (diesel udhaar)", credit: "Credit (payment received)", desc: "Narration" };
const ST = {
  matched: ["Matched", "bg-emerald-100 text-emerald-800"], amount_mismatch: ["Amount Mismatch", "bg-amber-100 text-amber-800"],
  missing_ours: ["Sirf Dealer statement me", "bg-rose-100 text-rose-800"], missing_dealer: ["Sirf hamare hisaab me", "bg-violet-100 text-violet-800"],
};

function DealerUpload({ pump, onDone }) {
  const [pv, setPv] = useState(null);
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState("");
  const [k, setK] = useState(0);
  const preview = async (f) => {
    setPv(null);
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(f.name.toLowerCase().endsWith(".pdf") ? "AI PDF padh raha hai…" : "Reading…");
    try { const { data } = await api.post("/diesel/dealer/preview", fd); setPv(data); setMap(data.mapping); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const run = async () => {
    setBusy("Importing…");
    try {
      const { data } = await api.post("/diesel/dealer/import", { preview_id: pv.preview_id, mapping: map, pump });
      toast.success(`${data.slips} slip · ${data.payments} payment lines · ${data.duplicates} duplicate · ${data.skipped} skipped`);
      setPv(null); setK(k + 1); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4" data-testid="dealer-upload">
      <Field label={`${pump} ka Dealer Statement (Excel / CSV / PDF)`}><Input key={k} type="file" accept=".csv,.xlsx,.xls,.pdf" data-testid="dealer-file-input" onChange={(e) => preview(e.target.files?.[0] || null)} /></Field>
      {busy && <p className="flex items-center gap-1 text-sm text-primary" data-testid="dealer-busy"><Sparkles className="h-4 w-4 animate-pulse" />{busy}</p>}
      {pv && <>
        <div className="text-sm font-bold">{pv.ai ? "AI ne PDF se rows nikali" : "Columns match karein"} — {pv.total_rows} rows</div>
        <div className="grid gap-3 sm:grid-cols-4 lg:grid-cols-7">{pv.fields.map((f) => <Field key={f} label={LABELS[f]}><select data-testid={`dlmap-${f}`} className="native-select" value={map[f] ?? ""} onChange={(e) => setMap({ ...map, [f]: e.target.value === "" ? undefined : Number(e.target.value) })}>
          <option value="">— none —</option>{pv.headers.map((h, j) => <option key={j} value={j}>{h}</option>)}</select></Field>)}</div>
        <TableWrap><thead><tr>{pv.headers.map((h, j) => <th key={j} className={th}>{h}</th>)}</tr></thead>
          <tbody>{pv.sample.map((r, i) => <tr key={i} className={tr}>{r.map((c, j) => <td key={j} className={`${td} whitespace-nowrap text-xs`}>{c}</td>)}</tr>)}</tbody></TableWrap>
        <Button data-testid="dealer-import-btn" disabled={!!busy} onClick={run}><Upload className="mr-1 h-4 w-4" />Import & Match</Button>
      </>}
    </div>
  );
}

function Summary({ s }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="dealer-summary">
      <StatCard testid="dealer-matched" label="Matched" value={s.matched} />
      <StatCard testid="dealer-mismatch" label="Amount Mismatch" value={s.amount_mismatch} accent={s.amount_mismatch ? "text-amber-600" : ""} />
      <StatCard testid="dealer-missing-ours" label="Sirf Dealer me" value={s.missing_ours} accent={s.missing_ours ? "text-rose-600" : ""} />
      <StatCard testid="dealer-missing-dealer" label="Sirf hamare me" value={s.missing_dealer} accent={s.missing_dealer ? "text-violet-600" : ""} />
      <div className="col-span-2 rounded-xl border bg-card p-3 text-sm lg:col-span-4" data-testid="dealer-balance">
        {fmtDate(s.from)} – {fmtDate(s.to)} · Dealer: udhaar {inr(s.dealer_slips, 2)} − mila {inr(s.dealer_paid, 2)} = <b>{inr(s.dealer_slips - s.dealer_paid, 2)}</b>
        {" "}· Hamara: udhaar {inr(s.our_slips, 2)} − diya {inr(s.our_paid, 2)} = <b>{inr(s.our_slips - s.our_paid, 2)}</b>
        {" "}· Farq: <b className={Math.abs(s.difference) > 1 ? "text-rose-600" : "text-emerald-700"} data-testid="dealer-difference">{inr(s.difference, 2)}</b>
      </div>
    </div>
  );
}

function ReconTable({ lines, onAdd }) {
  return (
    <TableWrap>
      <thead><tr>{["Date", "Type", "Slip / Ref", "Vehicle", "Dealer Amount", "Hamara Amount", "Status", "Card / Trip", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
      <tbody>{lines.map((x) => {
        const [lbl, cls] = ST[x.status];
        const mine = x.status === "missing_dealer";
        return (
          <tr key={x.id} className={tr} data-testid={`dealer-line-${x.id}`}>
            <td className={`${td} whitespace-nowrap`}>{fmtDate(x.date)}</td><td className={td}>{x.kind === "slip" ? "Diesel udhaar" : "Payment"}</td>
            <td className={`${td} font-mono text-xs`}>{x.slip_no || "-"}{x.desc && <div className="font-sans text-[10px] text-muted-foreground">{x.desc}</div>}</td>
            <td className={`${td} font-mono text-xs`}>{x.vrn || x.ours?.vehicle || "-"}</td>
            <td className={`${td} amount`}>{mine ? "-" : inr(x.amount, 2)}</td>
            <td className={`${td} amount`}>{mine ? inr(x.amount, 2) : x.ours ? inr(x.ours.amount, 2) : "-"}</td>
            <td className={td}><span className={`rounded px-2 py-0.5 text-[11px] font-bold ${cls}`} data-testid={`dealer-status-${x.id}`}>{lbl}</span></td>
            <td className={`${td} text-xs`}>{x.ours?.via && <>{x.ours.via}{x.ours.card && <span className="ml-1 rounded bg-sky-100 px-1 text-[10px] font-bold text-sky-800">CARD</span>}</>}
              {x.ours?.lr_number ? <span className="font-mono">{x.ours.lr_number}</span> : x.kind === "slip" && x.ours && <span className="text-muted-foreground">Unassigned</span>}</td>
            <td className={td}>{x.added_txn_id ? <span className="text-xs font-semibold text-emerald-700" data-testid={`dealer-added-${x.id}`}>Udhaar me joda</span>
              : x.status === "missing_ours" && x.kind === "slip" && <Button size="sm" data-testid={`dealer-add-${x.id}`} onClick={() => onAdd(x)}><Plus className="mr-1 h-3.5 w-3.5" />Udhaar me jodein</Button>}</td>
          </tr>);
      })}</tbody>
    </TableWrap>
  );
}

export function DealerRecon({ isAdmin }) {
  const [pumps, setPumps] = useState([]);
  const [pump, setPump] = useState("");
  const [d, setD] = useState(null);
  const [imps, setImps] = useState([]);
  useEffect(() => { api.get("/diesel/options").then((r) => { setPumps(r.data.pumps); setPump(r.data.pumps[0] || ""); }).catch(() => {}); }, []);
  const load = () => {
    if (!pump) return;
    api.get(`/diesel/dealer/recon?pump=${encodeURIComponent(pump)}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/diesel/dealer/imports?pump=${encodeURIComponent(pump)}`).then((r) => setImps(r.data)).catch(() => {});
  };
  useEffect(() => { setD(null); load(); }, [pump]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (x) => { try { await api.post(`/diesel/dealer/lines/${x.id}/add`); toast.success("Slip udhaar aur Unassigned Inbox me jud gayi"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const delImp = async (x) => { if (!window.confirm(`Delete statement ${x.file_name}?`)) return; try { await api.delete(`/diesel/dealer/imports/${x.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!pumps.length) return <Empty testid="dealer-no-pumps" text={'Pehle "Pump & Wallet" me pump jodein'} />;
  return (
    <div className="space-y-4" data-testid="dealer-recon">
      <Field label="Pump / Dealer" className="max-w-sm"><select data-testid="dealer-pump-select" className="native-select" value={pump} onChange={(e) => setPump(e.target.value)}>{pumps.map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
      <DealerUpload pump={pump} onDone={load} />
      {imps.length > 0 && <div className="flex flex-wrap gap-2 text-xs" data-testid="dealer-imports">{imps.map((x) => (
        <span key={x.id} className="flex items-center gap-1 rounded-full border px-3 py-1">{x.file_name} · {x.result?.slips ?? 0} slip · {x.result?.payments ?? 0} payment
          {isAdmin && <button type="button" title="Delete statement" data-testid={`dealer-import-del-${x.id}`} onClick={() => delImp(x)}><Trash2 className="h-3 w-3 text-rose-600" /></button>}</span>))}</div>}
      {d?.summary && <Summary s={d.summary} />}
      {d?.summary && <ReconTable lines={d.lines} onAdd={add} />}
      {d && !d.summary && <Empty testid="dealer-empty" text="Is pump ka statement abhi upload nahi hua" />}
    </div>
  );
}
